import { Link } from 'react-router-dom';
import Header from "@/landing/components/Header";

const PrivacyPage = () => {
    return (
        <div className="bg-background text-foreground font-sans min-h-screen transition-colors duration-300">
            {/* Navbar */}
            <Header />

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-12 pt-32">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground mb-12">Effective Date: February 2026</p>

                <section className="space-y-12 leading-relaxed text-[0.95rem] md:text-base text-foreground/90">
                    <p>
                        LinkedHash Inc. ("LinkedHash," "we," "us," or "our") operates the LinkedHash
                        MCP Server and related services. This Privacy Policy describes how we collect,
                        use, and protect your information when you use our services.
                    </p>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">1. Information We Collect</h2>
                        <p className="mb-4">We collect the following types of information:</p>
                        <ul className="list-disc pl-6 space-y-3">
                            <li><strong>Account Information:</strong> Your name and email address, provided when you sign in via Google or GitHub.</li>
                            <li><strong>Usage Data:</strong> Information about how you use our tools, including which tools are invoked, watch configurations, and evaluation results.</li>
                            <li><strong>Financial Queries:</strong> The ticker symbols, date ranges, and parameters you submit to our analysis tools. We do not store the full content of your Claude conversations.</li>
                            <li><strong>Billing Information:</strong> Payment details are collected and processed by Stripe. We store only your Stripe customer ID and subscription status — never your card number.</li>
                        </ul>
                        <div className="mt-6 p-5 bg-primary/5 border-l-4 border-primary rounded-r-lg text-sm text-muted-foreground">
                            <strong className="text-foreground font-semibold">Important:</strong> LinkedHash provides financial data and analysis tools.
                            Nothing in our service constitutes financial advice, investment recommendations, or solicitation to buy or sell securities.
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">2. How We Use Information</h2>
                        <p className="mb-4">We use the information we collect to:</p>
                        <ul className="list-disc pl-6 space-y-3">
                            <li>Provide, maintain, and improve the LinkedHash service.</li>
                            <li>Evaluate watch conditions and deliver alerts.</li>
                            <li>Enforce plan quotas and entitlements.</li>
                            <li>Process billing and manage subscriptions via Stripe.</li>
                            <li>Communicate service updates, policy changes, and support responses.</li>
                            <li>Detect and prevent abuse or unauthorized access.</li>
                        </ul>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">3. Data Storage</h2>
                        <p>
                            Your account data and watch configurations are stored in MongoDB Atlas,
                            hosted on US-based servers. All data is encrypted at rest using AES-256
                            encryption. Database connections use TLS to encrypt data in transit.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">4. Third-Party Services</h2>
                        <p className="mb-4">We use the following third-party services:</p>
                        <ul className="list-disc pl-6 space-y-3">
                            <li><strong className="text-foreground">Stripe</strong> — Payment processing. Stripe's privacy policy applies to all payment data.</li>
                            <li><strong className="text-foreground">Google and GitHub</strong> — OAuth authentication. We receive only your basic profile information during sign-in.</li>
                            <li><strong className="text-foreground">Polygon.io</strong> — Real-time and historical market data for stocks and options.</li>
                            <li><strong className="text-foreground">TwelveData</strong> — Technical indicators and supplementary market data.</li>
                            <li><strong className="text-foreground">MongoDB Atlas</strong> — Cloud database hosting.</li>
                        </ul>
                        <p className="mt-6">
                            We do not sell your personal information to any third party. Market data
                            providers receive only the query parameters (ticker symbols, date ranges)
                            needed to fulfill your requests — never your identity or account details.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">5. Data Retention</h2>
                        <ul className="list-disc pl-6 space-y-3">
                            <li><strong className="text-foreground">Active accounts:</strong> Your account data and watch history are retained for as long as your account is active.</li>
                            <li><strong className="text-foreground">Account closure:</strong> Upon account deletion, all personal data and watch history are permanently deleted within 30 days.</li>
                            <li><strong className="text-foreground">Trial expiration:</strong> If you do not upgrade after your trial ends, your watches are paused. Account data is retained for 90 days before automatic deletion.</li>
                            <li><strong className="text-foreground">Billing records:</strong> Transaction records required for tax and legal compliance may be retained for up to 7 years, as required by law.</li>
                        </ul>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-foreground pb-2 border-b border-border">6. Your Rights</h2>
                        <p className="mb-4">You have the right to:</p>
                        <ul className="list-disc pl-6 space-y-3">
                            <li><strong>Access</strong> — Request a copy of the personal data we hold about you.</li>
                            <li><strong>Correction</strong> — Request correction of inaccurate personal data.</li>
                            <li><strong>Deletion</strong> — Request deletion of your account and all associated data.</li>
                            <li><strong>Export</strong> — Request a machine-readable export of your data.</li>
                        </ul>
                        <p className="mt-6">
                            To exercise any of these rights, contact us at <a href="mailto:support@linkedhash.com" className="text-primary hover:underline">support@linkedhash.com</a>.
                            We will respond within 30 days.
                        </p>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="mt-16 py-12 border-t border-border text-center text-muted-foreground text-sm bg-card/30">
                <div className="flex justify-center space-x-6 mb-4">
                    <Link to="/" className="hover:text-foreground hover:underline transition-colors">Home</Link>
                    <Link to="/privacy" className="text-foreground font-semibold hover:underline">Privacy Policy</Link>
                    <a href="mailto:support@linkedhash.com" className="hover:text-foreground hover:underline transition-colors">Contact</a>
                </div>
                <p className="text-xs">&copy; 2026 LinkedHash Inc. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PrivacyPage;
